import { useEffect, useMemo } from "react";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import {
  ALL_AGREEMENT_TYPES,
  type AgreementTypeRecord,
} from "../../../apollo/agreementTypes";

type JurisdictionKind = "national" | "state" | "county" | "city" | "zip";

const JURISDICTION_KINDS: { value: JurisdictionKind; label: string }[] = [
  { value: "national", label: "National" },
  { value: "state", label: "State" },
  { value: "county", label: "County" },
  { value: "city", label: "City" },
  { value: "zip", label: "ZIP code" },
];

const JURISDICTION_VALUES = gql`
  query JurisdictionValuesForSettings($kind: String!, $state: String) {
    jurisdictionValues(kind: $kind, state: $state)
  }
`;

function titleCase(s: string): string {
  return s
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\B\w+/g, (w) => w.toLowerCase());
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface SettingsPanelProps {
  name: string;
  agreementType: string;
  agreementTypeId: string | null;
  jurisdictionKind: string;
  state: string;
  jurisdictionValue: string;
  isActive: boolean;
  onChangeName: (next: string) => void;
  onChangeJurisdictionKind: (next: string) => void;
  onChangeState: (next: string) => void;
  onChangeJurisdictionValue: (next: string) => void;
  onChangeActive: (next: boolean) => void;
}

export function SettingsPanel({
  name,
  agreementType,
  agreementTypeId,
  jurisdictionKind,
  state,
  jurisdictionValue,
  isActive,
  onChangeName,
  onChangeJurisdictionKind,
  onChangeState,
  onChangeJurisdictionValue,
  onChangeActive,
}: SettingsPanelProps) {
  const kind = (jurisdictionKind || "national") as JurisdictionKind;

  const needsState =
    kind === "state" ||
    kind === "county" ||
    kind === "city" ||
    kind === "zip";

  const needsValue =
    kind === "county" || kind === "city" || kind === "zip";

  // Fetch AgreementTypes to resolve the name
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" },
      fetchPolicy: "cache-and-network",
    },
  );

  const agreementTypeName = useMemo(() => {
    if (!agreementTypeId) return agreementType;
    const types = atQuery.data?.allAgreementTypes ?? [];
    const found = types.find((t) => t.id === agreementTypeId);
    return found?.name ?? agreementType;
  }, [agreementTypeId, agreementType, atQuery.data]);

  // Fetch states
  const [fetchStates, statesQuery] = useLazyQuery<{
    jurisdictionValues: string[];
  }>(JURISDICTION_VALUES);

  useEffect(() => {
    if (needsState) {
      void fetchStates({ variables: { kind: "state" } });
    }
  }, [needsState, fetchStates]);

  // Fetch values within state
  const [fetchValues, valuesQuery] = useLazyQuery<{
    jurisdictionValues: string[];
  }>(JURISDICTION_VALUES);

  useEffect(() => {
    if (needsValue && state) {
      void fetchValues({ variables: { kind, state } });
    }
  }, [needsValue, kind, state, fetchValues]);

  const statesList = statesQuery.data?.jurisdictionValues ?? [];
  const valuesList = valuesQuery.data?.jurisdictionValues ?? [];

  function handleKindChange(next: JurisdictionKind) {
    onChangeJurisdictionKind(next);
    onChangeState("");
    onChangeJurisdictionValue("");
  }

  function handleStateChange(next: string) {
    onChangeState(next);
    onChangeJurisdictionValue("");
  }

  return (
    <div className="space-y-5 max-w-md">
      <div>
        <h3 className="font-display text-[20px] font-medium tracking-tight">
          Identity & status
        </h3>
        <p className="text-[12.5px] text-ink-3 mt-1">
          Names and routing for this agreement. These don't affect how
          the document renders, so changes here don't require a fresh
          preview before saving.
        </p>
      </div>

      <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
        <label className="block">
          <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => onChangeName(e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
          />
        </label>

        <label className="block">
          <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
            Agreement type
          </span>
          <input
            type="text"
            value={agreementTypeName}
            readOnly
            className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] text-ink-4 cursor-not-allowed"
          />
          <span className="text-[10.5px] text-ink-5 mt-0.5 block">
            Set at creation. Clone this agreement to create a version under a different agreement type.
          </span>
        </label>

        {/* Jurisdiction picker */}
        <div>
          <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold block mb-1">
            Jurisdiction
          </span>
          <div className="space-y-3">
            <select
              value={kind}
              onChange={(e) =>
                handleKindChange(e.target.value as JurisdictionKind)
              }
              className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
            >
              {JURISDICTION_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>

            {needsState && (
              <select
                value={state}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                <option value="">Select state...</option>
                {statesList.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}

            {needsValue && state && (
              <select
                value={jurisdictionValue}
                onChange={(e) => onChangeJurisdictionValue(e.target.value)}
                className="w-full px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                <option value="">
                  Select {humanize(kind).toLowerCase()}...
                </option>
                {valuesList.map((v) => (
                  <option key={v} value={v}>
                    {titleCase(v.toLowerCase())}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span
            className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
              isActive ? "bg-sage" : "bg-ink-5"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-paper-hi transition-transform shadow-sm ${
                isActive ? "translate-x-4" : ""
              }`}
            />
            <input
              type="checkbox"
              className="sr-only"
              checked={isActive}
              onChange={(e) => onChangeActive(e.target.checked)}
              aria-label="Active"
            />
          </span>
          <span className="text-[13px]">
            <span className="font-medium">Active</span>
            <span className="text-ink-4 ml-2">
              {isActive
                ? "Customers will sign this version on new bookings."
                : "Hidden from the booking flow."}
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}
